import { forwardRef, useCallback, type InputHTMLAttributes } from 'react';
import type { FieldName } from './fieldLabel';
import './Field.css';

/** Input types drawn as a kit text field (`bk-control`); a range is the kit's `bk-range`; file / colour keep their
 *  native control. */
const TEXT_TYPES = new Set(['text', 'email', 'number', 'password', 'search', 'tel', 'url', 'date', 'time', 'datetime-local']);

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'aria-label' | 'aria-labelledby' | 'id'> &
  FieldName & {
    /** A short number (a page, a ratio, a rotation): centred, tabular figures, tight padding. Width is the host's. */
    numeric?: boolean;
    /** Edit in place (a layer name, a title): keeps the font of the text it edits, on the primary edge. */
    inline?: boolean;
    /** At least --bk-touch-min (44 px) on every pointer, like Button size="touch": the fields a Pencil (a fine
     *  pointer) uses in the rail and the media viewer. */
    touch?: boolean;
    id?: string;
  };

/** The kit notches a stepped range with 2-24 stops (DESIGN.md Range: more stops read as a texture). */
const NOTCH_MAX = 24;

/** The interval count of a range whose step makes a few whole stops ((max - min) / step in 2..24), else null. */
function rangeNotches(min: InputProps['min'], max: InputProps['max'], step: InputProps['step']): number | null {
  if (step === undefined || step === 'any') return null;
  const lo = Number(min ?? 0), hi = Number(max ?? 100), st = Number(step);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || !Number.isFinite(st) || st <= 0) return null;
  const n = (hi - lo) / st;
  const whole = Math.round(n);
  if (Math.abs(n - whole) > 1e-9 || whole < 2 || whole > NOTCH_MAX) return null;
  return whole;
}

/**
 * The text field (spec--pmt-ui-redesign 5(b)): the kit's `bk-control` -- square, hairline border, 28 px on a fine
 * pointer and 44 px under any coarse pointer (--bk-control-md), the --bk-focus ring. `type="range"` is the kit's
 * `bk-range` instead: a hairline track and a square ink thumb (kit >= 0.3.3); a `step` that makes 2-24 stops adds
 * the kit's dial notches (`bk-range--notched` + `--bk-range-steps`, kit >= 0.3.4, which the app pins). The custom
 * property is set through the ref, not a style prop: an inline style attribute is a source-scan
 * hit (src/styles/sourceScan.test.ts), and a caller's own `style` passes through untouched. A field is always named
 * (FieldName). A className on it is a hook that may place it (width, flex, margins), never draw it
 * (src/components/ui/oneInput.test.ts).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { numeric, inline, touch, className = '', type = 'text', ...props },
  ref
) {
  const kit = TEXT_TYPES.has(type) ? 'bk-control' : type === 'range' ? 'bk-range' : '';
  const notches = type === 'range' ? rangeNotches(props.min, props.max, props.step) : null;
  const setRef = useCallback(
    (el: HTMLInputElement | null) => {
      if (el) {
        if (notches !== null) el.style.setProperty('--bk-range-steps', String(notches));
        else el.style.removeProperty('--bk-range-steps');
      }
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref, notches]
  );
  return (
    <input
      ref={setRef}
      type={type}
      className={['input', kit, notches !== null ? 'bk-range--notched' : '', numeric ? 'input--numeric' : '', inline ? 'input--inline' : '', touch ? 'field--touch' : '', className].filter(Boolean).join(' ')}
      {...props}
    />
  );
});
